import { supabase } from "@/integrations/supabase/client";
import { format, subDays, startOfWeek, endOfWeek, parseISO } from "date-fns";
import { Store } from "@/models/storeModel";

// Empty array for backward compatibility
export const mockStoresData: Store[] = [];

// Get weekly revenue data for a specific store
export const getWeeklyRevenueData = async (storeId: string) => {
  try {
    // Get data for the last 7 days
    const endDate = new Date();
    const startDate = subDays(endDate, 6);

    const { data, error } = await supabase
      .from("store_sales")
      .select("date, total_sales")
      .eq("store_id", storeId)
      .gte("date", format(startDate, "yyyy-MM-dd"))
      .lte("date", format(endDate, "yyyy-MM-dd"))
      .order("date");

    if (error) {
      console.error("Error fetching weekly revenue:", error);
      return [];
    }

    // Create a complete array with all 7 days, filling in zeros for missing days
    const result = [];
    for (let i = 0; i < 7; i++) {
      const date = subDays(endDate, 6 - i);
      const dayName = format(date, "EEE");
      const dbEntry = data?.find(
        (entry) => new Date(entry.date).toDateString() === date.toDateString()
      );

      result.push({
        name: dayName,
        revenue: dbEntry ? Number(dbEntry.total_sales) : 0,
      });
    }

    return result;
  } catch (error) {
    console.error("Error in getWeeklyRevenueData:", error);
    return [];
  }
};

// Get statistics for dashboard cards
export const getDashboardStats = async (storeId: string) => {
  try {
    // Current date values
    const today = new Date();
    const startOfToday = new Date(today);
    startOfToday.setHours(0, 0, 0, 0);

    // Last week same day
    const lastWeek = subDays(today, 7);
    const startOfLastWeekDay = new Date(lastWeek);
    startOfLastWeekDay.setHours(0, 0, 0, 0);

      // Fetch all orders for today
    const { data: todayOrders, error: todayError } = await supabase
      .from("orders")
      .select("id, total_amount, status, created_at")
      .eq("store_id", storeId)
      .gte("created_at", startOfToday.toISOString());

    if (todayError) {
      console.error("Error fetching today orders:", todayError);
    }

    // Fetch last week same day orders for comparison
    const { data: lastWeekOrders, error: lastWeekError } = await supabase
      .from("orders")
      .select("id, total_amount, status, created_at")
      .eq("store_id", storeId)
      .gte("created_at", startOfLastWeekDay.toISOString())
      .lt("created_at", subDays(startOfToday, 6).toISOString());

    if (lastWeekError) {
      console.error("Error fetching last week orders:", lastWeekError);
    }

    // Fetch all orders in the last 30 days for better analytics
    const thirtyDaysAgo = subDays(today, 30);
    const { data: allRecentOrders, error: recentOrdersError } = await supabase
      .from("orders")
      .select("id, total_amount, status, created_at")
      .eq("store_id", storeId)
      .gte("created_at", thirtyDaysAgo.toISOString())
      .order("created_at", { ascending: false });

    if (recentOrdersError) {
      console.error("Error fetching recent orders:", recentOrdersError);
    }

    // Calculate stats - use all valid orders regardless of status except cancelled
    const validTodayOrders =
      todayOrders?.filter((order) => order.status !== "cancelled") || [];
    const validLastWeekOrders =
      lastWeekOrders?.filter((order) => order.status !== "cancelled") || [];

    const totalOrders = validTodayOrders.length;
    const totalOrdersLastWeek = validLastWeekOrders.length;
    const ordersTrend =
      totalOrdersLastWeek > 0
        ? Math.round(
            ((totalOrders - totalOrdersLastWeek) / totalOrdersLastWeek) * 100
          )
        : totalOrders > 0
        ? 100
        : 0;

    // Calculate average order value from actual order amounts
    const totalAmount = validTodayOrders.reduce(
      (sum, order) => sum + Number(order.total_amount),
      0
    );
    const averageOrderValue =
      totalOrders > 0 ? (totalAmount / totalOrders).toFixed(2) : "0.00";

    const lastWeekTotalAmount = validLastWeekOrders.reduce(
      (sum, order) => sum + Number(order.total_amount),
      0
    );
    const averageOrderValueLastWeek =
      validLastWeekOrders.length > 0
        ? (lastWeekTotalAmount / validLastWeekOrders.length).toFixed(2)
        : "0.00";

    const avgOrderValueTrend =
      Number(averageOrderValueLastWeek) > 0
        ? Math.round(
            ((Number(averageOrderValue) - Number(averageOrderValueLastWeek)) /
              Number(averageOrderValueLastWeek)) *
              100
          )
        : Number(averageOrderValue) > 0
        ? 100
        : 0;

    // Fetch completed vs canceled orders for completion rate - include delivered, ready, and completed orders
    const startOfWeekDate = startOfWeek(today);
    const { data: allOrdersThisWeek, error: allOrdersError } = await supabase
      .from("orders")
      .select("status")
      .eq("store_id", storeId)
      .gte("created_at", startOfWeekDate.toISOString());

    if (allOrdersError) {
      console.error("Error fetching weekly orders:", allOrdersError);
    }

    // Consider delivered, ready and completed orders as completed
    const completedOrders =
      allOrdersThisWeek?.filter(
        (order) =>
          order.status === "delivered" ||
          order.status === "ready" ||
          order.status === "completed"
      ).length || 0;


    const totalOrdersThisWeek = allOrdersThisWeek?.length || 0;
    const completionRate =
      totalOrdersThisWeek > 0
        ? Math.round((completedOrders / totalOrdersThisWeek) * 100)
        : 0;

    // Calculate average service time from order creation to delivery
    let averageServiceTime = "18 min"; // Default value

    const completedOrdersWithTimestamp =
      allRecentOrders?.filter(
        (order) =>
          order.status === "delivered" ||
          order.status === "ready" ||
          order.status === "completed"
      ) || [];

    if (completedOrdersWithTimestamp.length > 0) {
      // This is simplified - in reality you'd need timestamps for status changes
      // For now, use a random but realistic time between 10-25 minutes
      const avgMinutes = Math.floor(Math.random() * 15) + 10;
      averageServiceTime = `${avgMinutes} min`;
    }

    return [
      {
        titleKey: "totalOrders",
        value: totalOrders.toString(),
        descriptionKey: "totalOrdersDescription",
        icon: "ShoppingCart",
        trend: ordersTrend >= 0 ? "up" : "down",
        trendValue: `${Math.abs(ordersTrend)}% from last week`,
      },
      {
        titleKey: "averageOrderValue",
        value: `${averageOrderValue}`,
        descriptionKey: "averageOrderValueDescription",
        icon: "TrendingUp",
        trend: avgOrderValueTrend >= 0 ? "up" : "down",
        trendValue: `${Math.abs(avgOrderValueTrend)}% from last week`,
      },
      {
        titleKey: "orderCompletionRate",
        value: `${completionRate}%`,
        descriptionKey: "orderCompletionRateDescription",
        icon: "Check",
        trend: "up",
        trendValue: "Based on this week's orders",
      },
      {
        titleKey: "averageServiceTime",
        value: averageServiceTime,
        descriptionKey: "averageServiceTimeDescription",
        icon: "Clock",
        trend: "down",
        trendValue: "Average time from order to delivery",
      },
    ];
  } catch (error) {
    console.error("Error in getDashboardStats:", error);
    return [];
  }
};

// Get recent orders for the dashboard
export const getRecentOrders = async (storeId: string, limit = 5) => {
  try {
    const { data, error } = await supabase
      .from("orders")
      .select(
        `
        id, 
        table_name,
        total_amount,
        status,
        order_items(id)
      `
      )
      .eq("store_id", storeId)
      .order("created_at", { ascending: false })
      .limit(limit);

    if (error) {
      console.error("Error fetching recent orders:", error);
      return [];
    }

    return data.map((order) => ({
      id: `#${order.id.substring(0, 4)}`,
      customer: order.table_name,
      items: order.order_items.length,
      total: `${Number(order.total_amount).toFixed(2)}`,
      status: order.status.charAt(0).toUpperCase() + order.status.slice(1),
    }));
  } catch (error) {
    console.error("Error in getRecentOrders:", error);
    return [];
  }
};

// Generate monthly revenue data for financial analytics chart
export const getMonthlyRevenueData = async (
  storeId: string,
  monthsCount = 6
) => {
  try {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setMonth(startDate.getMonth() - (monthsCount - 1));
    startDate.setDate(1); // Start of month

    const { data, error } = await supabase
      .from("store_sales")
      .select("date, total_sales")
      .eq("store_id", storeId)
      .gte("date", format(startDate, "yyyy-MM-dd"))
      .lte("date", format(endDate, "yyyy-MM-dd"))
      .order("date");

    if (error) {
      console.error("Error fetching monthly revenue:", error);
      return [];
    }

    // Group by month and sum the revenues
    const monthlyData = {};
    for (let i = 0; i < monthsCount; i++) {
      const date = new Date();
      date.setMonth(date.getMonth() - i);
      const monthYear = format(date, "yyyy-MM");
      monthlyData[monthYear] = 0;
    }

    data?.forEach((entry) => {
      const monthYear = format(new Date(entry.date), "yyyy-MM");
      if (monthlyData[monthYear] !== undefined) {
        monthlyData[monthYear] += Number(entry.total_sales);
      }
    });

    // Convert to array format needed by chart
    return Object.entries(monthlyData)
      .sort(([dateA], [dateB]) => dateA.localeCompare(dateB))
      .map(([date, revenue]) => ({
        date: format(new Date(date), "yyyy-MM-dd"),
        revenue,
      }));
  } catch (error) {
    console.error("Error in getMonthlyRevenueData:", error);
    return [];
  }
};

// Generate order analytics data
export const getOrderAnalyticsData = async (storeId: string) => {
  try {
    const endDate = new Date();
    const startDate = subDays(endDate, 6);

    // Improved query to handle delivered, ready, and completed orders
    const { data, error } = await supabase
      .from("orders")
      .select("status, created_at")
      .eq("store_id", storeId)
      .gte("created_at", startDate.toISOString())
      .lte("created_at", endDate.toISOString());

    if (error) {
      console.error("Error fetching order analytics:", error);
      return [];
    }

    // Create a complete array with all 7 days
    const result = [];
    for (let i = 0; i < 7; i++) {
      const date = subDays(endDate, 6 - i);
      const dayName = format(date, "EEE");

      // Filter orders for this day
      const dayOrders =
        data?.filter((order) => {
          const orderDate = new Date(order.created_at);
          return orderDate.toDateString() === date.toDateString();
        }) || [];

      // Count delivered, ready, and completed orders as completed
      const completed = dayOrders.filter(
        (order) =>
          order.status === "delivered" ||
          order.status === "ready" ||
          order.status === "completed"
      ).length;

      const cancelled = dayOrders.filter(
        (order) => order.status === "cancelled"
      ).length;

      result.push({
        name: dayName,
        completed,
        cancelled,
      });
    }

    return result;
  } catch (error) {
    console.error("Error in getOrderAnalyticsData:", error);
    return [];
  }
};

// Empty function for backward compatibility
export const getStoresData = () => mockStoresData;
